import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
  Avatar
} from '@mui/material';
import {
  MoneyOffOutlined,
  PersonOffOutlined,
  SecurityOutlined,
  SpeedOutlined,
  CheckCircleOutlined,
  CancelOutlined
} from '@mui/icons-material';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

const Features = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useLanguage();

  const advantages = [
    {
      icon: <MoneyOffOutlined />,
      title: t('features.advantages.payPerUse.title'),
      privacv: t('features.advantages.payPerUse.privacv'),
      others: t('features.advantages.payPerUse.others'),
      highlight: t('features.advantages.payPerUse.highlight'),
      category: 'financial'
    },
    {
      icon: <SecurityOutlined />,
      title: t('features.advantages.zeroDataCollection.title'),
      privacv: t('features.advantages.zeroDataCollection.privacv'),
      others: t('features.advantages.zeroDataCollection.others'),
      highlight: t('features.advantages.zeroDataCollection.highlight'),
      category: 'privacy'
    },
    {
      icon: <SpeedOutlined />,
      title: t('features.advantages.instantAccess.title'),
      privacv: t('features.advantages.instantAccess.privacv'),
      others: t('features.advantages.instantAccess.others'),
      highlight: t('features.advantages.instantAccess.highlight'),
      category: 'experience'
    },
    {
      icon: <PersonOffOutlined />,
      title: t('features.advantages.noRegistration.title'),
      privacv: t('features.advantages.noRegistration.privacv'),
      others: t('features.advantages.noRegistration.others'),
      highlight: t('features.advantages.noRegistration.highlight'),
      category: 'privacy'
    }
  ];

  const additionalBenefits = [
    {
      title: t('features.additionalBenefits.professionalQuality.title'),
      description: t('features.additionalBenefits.professionalQuality.description'),
      value: t('features.additionalBenefits.professionalQuality.value')
    },
    {
      title: t('features.additionalBenefits.fullFeatureAccess.title'),
      description: t('features.additionalBenefits.fullFeatureAccess.description'),
      value: t('features.additionalBenefits.fullFeatureAccess.value')
    },
    {
      title: t('features.additionalBenefits.offlineCapable.title'),
      description: t('features.additionalBenefits.offlineCapable.description'),
      value: t('features.additionalBenefits.offlineCapable.value')
    },
    {
      title: t('features.additionalBenefits.noVendorLockIn.title'),
      description: t('features.additionalBenefits.noVendorLockIn.description'),
      value: t('features.additionalBenefits.noVendorLockIn.value')
    },
    {
      title: t('features.additionalBenefits.gdprCompliant.title'),
      description: t('features.additionalBenefits.gdprCompliant.description'),
      value: t('features.additionalBenefits.gdprCompliant.value')
    },
    {
      title: t('features.additionalBenefits.noSpamEver.title'),
      description: t('features.additionalBenefits.noSpamEver.description'),
      value: t('features.additionalBenefits.noSpamEver.value')
    }
  ];

  return (
    <Box
      id="features"
      sx={{
        py: { xs: 5, md: 16 },
        background: `linear-gradient(135deg, ${tokens.colors.background.alt} 0%, white 100%)`,
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 12 } }}>
          <Typography
            variant="overline"
            sx={{
              color: tokens.colors.primary.main,
              fontWeight: tokens.typography.fontWeights.bold,
              letterSpacing: '0.1em',
              fontSize: '0.75rem',
              mb: 2,
              display: 'block'
            }}
                      >
              {t('features.sectionTitle')}
            </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.6rem', md: '3.5rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.text.primary,
              mb: { xs: 1.5, md: 3 },
              lineHeight: 1.1
            }}
                      >
              {t('features.title')}
            </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9rem', md: '1.25rem' },
              color: tokens.colors.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.5,
              fontWeight: tokens.typography.fontWeights.regular
            }}
          >
            {t('features.subtitle')}
          </Typography>
        </Box>

        {/* Desktop Comparison Table */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: tokens.radius.xl,
            boxShadow: tokens.shadows.lg,
            overflow: 'hidden',
            mb: { xs: 4, md: 8 },
            display: { xs: 'none', md: 'block' }
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${tokens.colors.primary.main} 0%, ${tokens.colors.primary.dark} 100%)`,
              p: tokens.spacing[4]
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item md={4}>
                <Typography
                  variant="h6"
                  sx={{
                    color: tokens.colors.primary.contrast,
                    fontWeight: tokens.typography.fontWeights.bold,
                    fontSize: tokens.typography.fontSizes.lg
                  }}
                >
                  Core Advantages
                </Typography>
              </Grid>
              <Grid item md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: tokens.colors.primary.contrast,
                      fontWeight: tokens.typography.fontWeights.bold,
                      fontSize: tokens.typography.fontSizes.lg
                    }}
                  >
                    privacv
                  </Typography>
                </Box>
              </Grid>
              <Grid item md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: tokens.colors.alpha.white[800],
                      fontWeight: tokens.typography.fontWeights.bold,
                      fontSize: tokens.typography.fontSizes.lg
                    }}
                  >
                    Other Resume Builders
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Body */}
          {advantages.map((advantage, index) => (
            <Fade in timeout={600 + index * 150} key={index}>
              <Box
                sx={{
                  borderBottom: index < advantages.length - 1 ? `1px solid ${tokens.colors.borders.light}` : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: `${tokens.colors.primary.main}02`
                  }
                }}
              >
                <Grid container spacing={2} alignItems="center" sx={{ p: tokens.spacing[4] }}>
                  {/* Feature Column */}
                  <Grid item md={4}>
                    <Stack direction="row" alignItems="center" spacing={tokens.spacing[2]}>
                      <Avatar
                        sx={{
                          width: tokens.spacing[10],
                          height: tokens.spacing[10],
                          backgroundColor: `${tokens.colors.primary.main}10`,
                          color: tokens.colors.primary.main
                        }}
                      >
                        {React.cloneElement(advantage.icon, { sx: { fontSize: tokens.typography.fontSizes.xl } })}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: tokens.typography.fontWeights.semibold,
                            color: tokens.colors.text.primary,
                            fontSize: tokens.typography.fontSizes.lg,
                            mb: tokens.spacing[0.5],
                            lineHeight: tokens.typography.lineHeights.tight
                          }}
                        >
                          {advantage.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: tokens.colors.primary.main,
                            fontSize: tokens.typography.fontSizes.sm,
                            fontWeight: tokens.typography.fontWeights.medium
                          }}
                        >
                          {advantage.highlight}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* privacv Column */}
                  <Grid item md={4}>
                    <Stack alignItems="center" spacing={tokens.spacing[1]}>
                      <CheckCircleOutlined
                        sx={{
                          color: tokens.colors.success,
                          fontSize: tokens.typography.fontSizes['3xl']
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: 'center',
                          fontWeight: tokens.typography.fontWeights.semibold,
                          color: tokens.colors.success,
                          fontSize: tokens.typography.fontSizes.base,
                          lineHeight: tokens.typography.lineHeights.tight
                        }}
                      >
                        {advantage.privacv}
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* Others Column */}
                  <Grid item md={4}>
                    <Stack alignItems="center" spacing={tokens.spacing[1]}>
                      <CancelOutlined
                        sx={{
                          color: tokens.colors.error,
                          fontSize: tokens.typography.fontSizes['3xl']
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: 'center',
                          fontWeight: tokens.typography.fontWeights.medium,
                          color: tokens.colors.error,
                          fontSize: tokens.typography.fontSizes.base,
                          lineHeight: tokens.typography.lineHeights.tight
                        }}
                      >
                        {advantage.others}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Fade>
          ))}
        </Box>

        {/* Mobile Simplified Cards */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            mb: 3
          }}
        >
          <Grid container spacing={1.5}>
            {advantages.map((advantage, index) => (
              <Grid item xs={12} key={index}>
                <Fade in timeout={600 + index * 150}>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: tokens.radius.base,
                      boxShadow: tokens.shadows.sm,
                      p: 1.5,
                      border: `1px solid ${tokens.colors.borders.light}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: tokens.shadows.md,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: `${tokens.colors.primary.main}10`,
                          color: tokens.colors.primary.main
                        }}
                      >
                        {React.cloneElement(advantage.icon, { sx: { fontSize: '0.9rem' } })}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: tokens.typography.fontWeights.semibold,
                            color: tokens.colors.text.primary,
                            fontSize: '0.85rem',
                            lineHeight: 1.2
                          }}
                        >
                          {advantage.title}
                        </Typography>
                      </Box>
                      <CheckCircleOutlined
                        sx={{
                          color: tokens.colors.success,
                          fontSize: '1.1rem'
                        }}
                      />
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        color: tokens.colors.primary.main,
                        fontSize: '0.75rem',
                        fontWeight: tokens.typography.fontWeights.medium,
                        lineHeight: 1.3
                      }}
                    >
                      {advantage.highlight}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Additional Benefits Grid */}
        <Box sx={{ 
          mb: tokens.spacing[8],
          display: { xs: 'none', md: 'block' }
        }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              color: tokens.colors.text.primary,
              fontWeight: tokens.typography.fontWeights.bold,
              mb: tokens.spacing[6],
              fontSize: { xs: tokens.typography.fontSizes['2xl'], md: tokens.typography.fontSizes['3xl'] }
            }}
          >
            {t('features.additionalBenefits.title')}
          </Typography>
          
          <Grid container spacing={tokens.spacing[4]}>
            {additionalBenefits.map((benefit, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in timeout={1000 + index * 100}>
                  <Box
                    sx={{
                      p: tokens.spacing[4],
                      backgroundColor: tokens.colors.background.paper,
                      borderRadius: tokens.radius.lg,
                      border: `1px solid ${tokens.colors.borders.light}`,
                      height: '100%',
                      transition: tokens.transitions.smooth,
                      '&:hover': {
                        boxShadow: tokens.shadows.md,
                        borderColor: tokens.colors.primary.light,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: tokens.typography.fontWeights.semibold,
                        color: tokens.colors.text.primary,
                        mb: tokens.spacing[2],
                        fontSize: tokens.typography.fontSizes.lg
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: tokens.colors.text.secondary,
                        mb: tokens.spacing[2],
                        fontSize: tokens.typography.fontSizes.sm,
                        lineHeight: tokens.typography.lineHeights.normal
                      }}
                    >
                      {benefit.description}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: tokens.colors.primary.main,
                        fontWeight: tokens.typography.fontWeights.medium,
                        fontSize: tokens.typography.fontSizes.sm
                      }}
                    >
                      → {benefit.value}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Features; 